import type {EBirdObservationsByLocation} from "ebird-mydata-reader";
import {classifyBirdingHabitat, type BirdingHabitatProfile} from "./habitat-classification";
import {HABITAT_FAMILY_LABELS} from "./habitat-classification";
import type {HabitatFamilyLabel} from "./habitat-classification/constants";
import type {
    EnrichedObservationsByLocation,
    HotspotHabitatFeedbackSummary,
    HotspotHabitatOverride,
    HotspotHabitatTrustSignals
} from "./types";

type HabitatTrustLocation = EBirdObservationsByLocation | EnrichedObservationsByLocation;

function slugifyHabitatValue(value: string) {
    return value
        .trim()
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^\w]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

function normalizeOverrideFamily(value?: string): HabitatFamilyLabel | undefined {
    if (!value) return undefined;
    const normalized = value.trim().toLowerCase();

    return HABITAT_FAMILY_LABELS.find(label => label.toLowerCase() === normalized);
}

function prefixPossibly(value: string) {
    return value.startsWith('Possibly ') ? value : `Possibly ${value}`;
}

function applyClassifierTrustLabeling(
    habitatProfile: BirdingHabitatProfile | undefined,
    feedbackSummary?: HotspotHabitatFeedbackSummary
): BirdingHabitatProfile | undefined {
    if (!habitatProfile) {
        return undefined;
    }

    if (habitatProfile.source === 'human_override') {
        return habitatProfile;
    }

    if (feedbackSummary?.userConfirmedMatch) {
        return {
            ...habitatProfile,
            sourceLabel: 'User confirmed'
        };
    }

    return {
        ...habitatProfile,
        displayHabitat: prefixPossibly(habitatProfile.displayHabitat),
        confidence: habitatProfile.confidence === 'high' ? 'medium' : habitatProfile.confidence,
        sourceLabel: undefined
    };
}

function applyHotspotHabitatOverride(
    habitatProfile: BirdingHabitatProfile | undefined,
    habitatOverride?: HotspotHabitatOverride
): BirdingHabitatProfile | undefined {
    if (!habitatOverride) {
        return habitatProfile;
    }

    const overrideFamily = normalizeOverrideFamily(habitatOverride.overrideFamily);
    if (!overrideFamily) {
        return habitatProfile;
    }

    const baseHabitat = habitatOverride.overrideBaseHabitat?.trim() || habitatOverride.overrideHabitat;
    const displayHabitat = habitatOverride.overrideDisplayHabitat?.trim() || habitatOverride.overrideHabitat;
    const baseProfile = habitatProfile;

    return {
        ...(baseProfile ?? {
            version: 0,
            source: 'classifier',
            family: overrideFamily,
            habitat: baseHabitat,
            habitatCode: undefined,
            subtype: undefined,
            displayHabitat,
            modifiers: [],
            sourceBasis: {},
            primaryHabitat: baseHabitat,
            primaryHabitatSlug: '',
            habitatFamily: overrideFamily,
            secondaryHabitats: [],
            terrainSummary: undefined,
            waterContext: undefined,
            confidence: 'high' as const,
            reasons: [],
            sourceFields: [],
            explanation: {
                winner: undefined,
                nextMatches: [],
                matchedCount: 0,
                summary: '',
                debug: {
                    input: {
                        primaryHabitat: '',
                        secondaryHabitats: [],
                        habitatMixLabels: [],
                        habitatFamily: overrideFamily,
                        hotspotNameTerms: [],
                        coordinateTerms: [],
                        biomeTerms: [],
                        ecoregionTerms: [],
                        sourceFields: []
                    },
                    rankedMatches: [],
                    canonicalMatches: [],
                    derivedMatches: []
                }
            }
        }),
        source: 'human_override',
        sourceLabel: 'Human override',
        overrideReason: habitatOverride.reason,
        family: overrideFamily,
        habitat: baseHabitat,
        displayHabitat,
        primaryHabitat: baseHabitat,
        primaryHabitatSlug: slugifyHabitatValue(baseHabitat),
        habitatFamily: overrideFamily,
        confidence: 'high',
        reasons: habitatOverride.reason
            ? [`Human override: ${habitatOverride.reason}`]
            : ['Human override applied after habitat review'],
        explanation: {
            ...(baseProfile?.explanation ?? {
                winner: undefined,
                nextMatches: [],
                matchedCount: 0,
                summary: '',
                debug: {
                    input: {
                        primaryHabitat: '',
                        secondaryHabitats: [],
                        habitatMixLabels: [],
                        habitatFamily: overrideFamily,
                        hotspotNameTerms: [],
                        coordinateTerms: [],
                        biomeTerms: [],
                        ecoregionTerms: [],
                        sourceFields: []
                    },
                    rankedMatches: [],
                    canonicalMatches: [],
                    derivedMatches: []
                }
            }),
            summary: `Human override applied${habitatOverride.reason ? `: ${habitatOverride.reason}` : '.'} Classifier output remains available for audit.`,
            debug: {
                ...(baseProfile?.explanation.debug ?? {
                    input: {
                        primaryHabitat: '',
                        secondaryHabitats: [],
                        habitatMixLabels: [],
                        habitatFamily: overrideFamily,
                        hotspotNameTerms: [],
                        coordinateTerms: [],
                        biomeTerms: [],
                        ecoregionTerms: [],
                        sourceFields: []
                    },
                    rankedMatches: [],
                    canonicalMatches: [],
                    derivedMatches: []
                }),
                humanOverride: {
                    locationId: habitatOverride.locationId,
                    overrideHabitat: habitatOverride.overrideHabitat,
                    overrideFamily: habitatOverride.overrideFamily,
                    overrideDisplayHabitat: habitatOverride.overrideDisplayHabitat,
                    overrideBaseHabitat: habitatOverride.overrideBaseHabitat,
                    reason: habitatOverride.reason,
                    appliedAt: habitatOverride.appliedAt,
                    updatedAt: habitatOverride.updatedAt
                } as any
            }
        }
    };
}

export function getHotspotAverageCoordinates(location: HabitatTrustLocation) {
    const observations = Array.isArray((location as any).observations)
        ? ((location as any).observations as Array<{latitude?: number, longitude?: number}>)
        : [];
    const coordinates = observations.filter(
        (observation: {latitude?: number, longitude?: number}) => Number.isFinite(observation.latitude) && Number.isFinite(observation.longitude)
    );

    if (!coordinates.length) {
        const latitude = (location as any).latitude;
        const longitude = (location as any).longitude;

        if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
            return {
                latitude,
                longitude
            };
        }

        return undefined;
    }

    const totals = coordinates.reduce((accumulator: {latitude: number, longitude: number}, observation: {latitude?: number, longitude?: number}) => {
        const latitude = observation.latitude as number;
        const longitude = observation.longitude as number;
        accumulator.latitude += latitude;
        accumulator.longitude += longitude;
        return accumulator;
    }, {latitude: 0, longitude: 0});

    return {
        latitude: totals.latitude / coordinates.length,
        longitude: totals.longitude / coordinates.length
    };
}

export function buildHotspotHabitatTrustSignals(
    habitatProfile?: BirdingHabitatProfile,
    feedbackSummary?: HotspotHabitatFeedbackSummary
): HotspotHabitatTrustSignals | undefined {
    const classifierConfidence = habitatProfile?.confidence;
    const classifierHighConfidence = classifierConfidence === 'high';
    const userConfirmedMatch = Boolean(feedbackSummary?.userConfirmedMatch);

    if (!habitatProfile && !feedbackSummary) {
        return undefined;
    }

    return {
        classifierConfidence,
        classifierHighConfidence,
        userConfirmedMatch,
        trustedMatch: classifierHighConfidence || userConfirmedMatch
    };
}

export function buildHotspotHabitatProfile(location: HabitatTrustLocation) {
    const ecoData = 'ecoData' in location ? location.ecoData : undefined;
    const coordinates = getHotspotAverageCoordinates(location);
    const habitatOverride = 'habitatOverride' in location ? location.habitatOverride : undefined;
    const feedbackSummary = 'habitatFeedbackSummary' in location ? location.habitatFeedbackSummary : undefined;

    const habitatProfile = classifyBirdingHabitat(ecoData, {
        hotspotName: location.location,
        latitude: coordinates?.latitude,
        longitude: coordinates?.longitude
    });

    return applyClassifierTrustLabeling(
        applyHotspotHabitatOverride(habitatProfile, habitatOverride),
        feedbackSummary
    );
}
